import { DropResult } from "@hello-pangea/dnd";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import useSWRMutation from "swr/mutation";

import { UploadPhotos } from "@/components/shared/photoRegisterV2";
import { CREW_END_POINTS } from "@/constants/crew/endPoints";
import { OWNER_END_POINTS } from "@/constants/owner/endPoints";
import { AuthContext } from "@/contexts/auth";
import { Variant } from "@/hooks/common/photo/usePhotoRegister";

/* 写真をD&Dでソートする */
export const usePhotoSort = ({
  variant,
  photoList
}: {
  variant: Variant;
  photoList: UploadPhotos[];
}) => {
  const { token } = useContext(AuthContext);
  const [result, setResult] = useState<DropResult | null>(null);
  const [problemId, setProblemId] = useState<number | null>(null); //クルー報告の場合の問題ID
  const [targetId, setTargetId] = useState<number | null>(null); //クルー報告の場合のターゲットID
  const [sortPhotoList, setSortPhotoList] = useState<UploadPhotos[]>([]); //写真リスト

  //写真ソート用のエンドポイントURLを生成
  const createApiPath = () => {
    const SORT = "photos-sort";
    switch (variant) {
      case "report":
        //クルー：作業箇所報告
        return `${CREW_END_POINTS.TASK.SUB_REPORT}/${targetId}/${SORT}`;
      case "comments":
        //クルー：異常箇所報告
        return `${CREW_END_POINTS.TASK.SUB_REPORT}/${problemId}/comment/${targetId}/${SORT}`;
      case "property":
        //依頼者：物件情報
        return `${OWNER_END_POINTS.VENUE.DETAIL}/${targetId}/${SORT}`;
    }
    //TODO: CO-414 others / problems / final
  };

  //写真ソート用のリクエスト
  const sendRequest = async (
    url: string,
    {
      arg: { positions }
    }: {
      arg: {
        positions: {
          id: number;
          position: number | undefined;
        }[];
      };
    }
  ) => {
    try {
      await axios.post(url, { positions }, token);
    } catch (e) {
      console.error(e);
    }
  };

  //リクエスト実行
  const { trigger, isMutating } = useSWRMutation(createApiPath(), sendRequest);

  //写真ソート
  const sortPhotos = async (result: DropResult) => {
    // ドロップ先がない
    if (!result.destination) return;

    const tmpPhotoList = photoList.filter((p) => p.position);
    if (tmpPhotoList) {
      /* step1 - 並び替え対象の位置取得 */
      //[移動元]の写真情報の取得
      const source = result.source.index;
      const sorPhotoIndex = tmpPhotoList.findIndex(
        (s) => s.position === source
      );
      const sorPhotoInfo = tmpPhotoList[sorPhotoIndex];

      //[移動先]の写真情報の取得
      const destination = result.destination?.index;
      const destPhotoIndex = tmpPhotoList.findIndex(
        (d) => d.position === destination
      );
      const destPhotoInfo = tmpPhotoList[destPhotoIndex];

      /*
      step2 - 並び替え処理（通常:[1]<=>[2][3]）
      - 移動先・元の位置が同じ場合は実行しない
      */
      if (
        sorPhotoInfo &&
        destPhotoInfo &&
        destination &&
        source !== destination
      ) {
        /*
        ポジション入替/配列ソート(表示時、ポジションNo.が基準)
        - [0][1][2] => [2][0][1] = 移動距離が+-2 => 移動先ポジションは 元のポジション-移動距離+-1
        */

        //並び替え距離
        const distance = destination - source;

        //配列内ポジション値入替
        const sourceIndex = source - 1;
        const destIndex = destination - 1;
        const sortPhototArr = tmpPhotoList;
        let positions = [];

        if (distance === -1 || distance === 1) {
          //隣同士入替
          sortPhototArr[destIndex].position = source;
          sortPhototArr[sourceIndex].position = destination;
          //ポジション入替JSON生成
          positions = [
            {
              id: sorPhotoInfo.id,
              position: destination
            },
            {
              id: destPhotoInfo.id,
              position: source
            }
          ];
        } else {
          //2個以上先へ移動 (distance == +-2)
          const order = source < destination ? "back" : "front";
          const loopLimit =
            source < destination
              ? destination - sourceIndex
              : source - destIndex;

          //移動前後のポジションを調整
          for (let i = 0; i < loopLimit - 1; i++) {
            if (order === "front") {
              //前へ移動
              sortPhototArr[destIndex + i].position = destination + i + 1;
            } else {
              //後ろへ移動
              sortPhototArr[destIndex - i].position = destination - i - 1;
            }
          }
          //移動対象の写真ポジションを変更
          sortPhototArr[sourceIndex].position = destination;
          for (const sortArr of sortPhototArr) {
            positions.push({
              id: sortArr.id,
              position: sortArr.position
            });
          }
        }
        sortPhototArr.sort((a, b) => (a.position || 0) - (b.position || 0));

        //表示に先に反映
        setSortPhotoList(sortPhototArr);
        //並び替え結果を保存
        trigger({ positions });
      }
    }
  };

  //Hook
  useEffect(() => {
    (async () => {
      if (result) {
        await sortPhotos(result);
        setResult(null);
      }
    })();
  }, [result]);

  return {
    sortPhotoList,
    setSortResult: setResult, //D&Dの結果をセット
    setSortProblemId: setProblemId,
    setSortTargetId: setTargetId,
    isPhotoSortLoading: isMutating
  };
};
